<template>
  <base-nav
    container-classes="container-fluid"
    class="navbar-top navbar-expand custom-navbar"
    :class="{'navbar-dark': type === 'default'}"
  >
    <div class="d-flex align-items-center">
      <!-- Balance display with gradient background and card-like design -->
      <div class="balance-display d-none d-lg-block">
        <div class="balance-card">
          <div class="balance-header">{{ $t('navbar.totalBalance') }}</div>
          <div class="balance-amount">{{ formattedBalance }} <span>UZS</span></div>
        </div>
      </div>
    </div>

    <!-- Navbar links -->
    <b-navbar-nav class="align-items-center ml-md-auto">
      <!-- Search icon for small screens -->
      <li class="nav-item d-sm-none">
        <a class="nav-link" href="#" data-action="search-show" data-target="#navbar-search-main">
          <i class="ni ni-zoom-split-in"></i>
        </a>
      </li>
    </b-navbar-nav>

    <b-navbar-nav class="align-items-center ml-auto ml-md-0">
      <!-- Language switcher -->
      <li class="nav-item lang-switcher mr-2">
        <button
          v-for="loc in supportedLocales"
          :key="loc"
          type="button"
          class="lang-switcher__btn"
          :class="{ 'is-active': $i18n.locale === loc }"
          @click="onSetLocale(loc)"
          :title="loc.toUpperCase()"
        >{{ loc.toUpperCase() }}</button>
      </li>

      <base-dropdown
        menu-on-right
        class="nav-item user-dropdown"
        tag="li"
        title-tag="a"
        title-classes="nav-link pr-0"
      >
        <a href="#" class="nav-link pr-0 d-flex align-items-center" @click.prevent slot="title-container">
          <div class="avatar avatar-sm rounded-circle profile-avatar">
            <img alt="Image placeholder" src="img/theme/user1.png">
          </div>
          <b-media-body class="ml-2 d-none d-lg-block">
            <span class="mb-0 text-sm font-weight-bold">{{ currentUser.name }}</span>
          </b-media-body>
          <i class="ni ni-chevron-down ml-1 d-none d-lg-block"></i>
        </a>

        <template>
          <div class="dropdown-header">
            <h6 class="text-overflow m-0">{{ $t('navbar.welcome') }}</h6>
          </div>
          <b-dropdown-item href="#" @click.prevent="goToSettings">
            <i class="ni ni-settings-gear-65"></i>
            <span>{{ $t('navbar.settings') }}</span>
          </b-dropdown-item>
          <div class="dropdown-divider"></div>
          <b-dropdown-item href="#" @click.prevent="handleLogout" class="text-danger">
            <i class="ni ni-user-run"></i>
            <span>{{ $t('navbar.logout') }}</span>
          </b-dropdown-item>
        </template>
      </base-dropdown>
    </b-navbar-nav>
  </base-nav>
</template>

<script>
import { CollapseTransition } from 'vue2-transitions';
import { BaseNav, Modal } from '@/components';
import axios from 'axios';
import { mapGetters } from "vuex";
import { setLocale, SUPPORTED_LOCALES } from '@/i18n';

export default {
  components: {
    CollapseTransition,
    BaseNav,
    Modal
  },
  props: {
    type: {
      type: String,
      default: 'default', // default|light
      description: 'Look of the dashboard navbar. Default (Green) or light (gray)'
    }
  },
  computed: {
    ...mapGetters(["currentUser"]),
    formattedBalance() {
      // Format number with commas for thousands
      return this.totalBalance ? this.totalBalance.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",") : '0';
    }
  },
  data() {
    return {
      totalBalance: '',
      showMenu: false,
      searchModalVisible: false,
      searchQuery: '',
      supportedLocales: SUPPORTED_LOCALES
    };
  },
  methods: {
    onSetLocale(locale) {
      setLocale(locale);
    },
    handleLogout() {
      localStorage.removeItem('token');
      this.$router.push({ name: 'login' });
    },
    async showTotalBalance() {
      try {
        const response = await axios.get('dashboard/s-cards');
        this.totalBalance = response.data.total_balance;
      } catch (error) {
        console.error('Error fetching balance:', error);
        this.totalBalance = '0';
      }
    },
    goToSettings() {
      this.$router.push({ name: 'settings' });
    }
  },
  mounted() {
    this.showTotalBalance();
  }
};
</script>

<style scoped>
.custom-navbar {
  transition: all 0.2s ease;
}

.balance-display {
  margin-right: 20px;
}

.balance-card {
  position: relative;
  padding: 10px 18px;
  border-radius: 10px;
  background: linear-gradient(135deg, rgba(46, 204, 113, 0.8) 0%, rgba(52, 152, 219, 0.8) 100%);
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(5px);
  min-width: 200px;
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.balance-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 7px 14px rgba(0, 0, 0, 0.15);
}

.balance-header {
  font-size: 0.8rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.85);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.balance-amount {
  font-size: 1.3rem;
  font-weight: 700;
  color: white;
  margin-top: 2px;
}

.balance-amount span {
  font-size: 0.9rem;
  opacity: 0.9;
  font-weight: 500;
}

.user-dropdown .dropdown-menu {
  min-width: 210px;
  box-shadow: 0 50px 100px rgba(50, 50, 93, 0.1), 0 15px 35px rgba(50, 50, 93, 0.15), 0 5px 15px rgba(0, 0, 0, 0.1);
  border: none;
  border-radius: 0.375rem;
}

.profile-avatar {
  border: 2px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.profile-avatar:hover {
  transform: scale(1.05);
}

.dropdown-header {
  background-color: #f6f9fc;
  border-bottom: 1px solid #e9ecef;
}

.dropdown-divider {
  margin: 0.5rem 0;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  display: flex;
  align-items: center;
}

.dropdown-item i {
  margin-right: 10px;
  font-size: 1rem;
  width: 20px;
  text-align: center;
}

.dropdown-item:active, .dropdown-item:focus {
  background-color: #f7fafc;
}

.text-danger i, .text-danger span {
  color: #f5365c;
}

.lang-switcher {
  display: inline-flex;
  gap: 4px;
  padding: 3px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 8px;
}
.lang-switcher__btn {
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: rgba(255, 255, 255, 0.75);
  background: transparent;
  border: none;
  border-radius: 5px;
  padding: 5px 9px;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease, color 0.15s ease;
}
.lang-switcher__btn:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}
.lang-switcher__btn.is-active {
  background: rgba(255, 255, 255, 0.95);
  color: #1a174d;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.18);
}
</style>
